import { createClient } from '@supabase/supabase-js';

import type { ApiChat } from '../../api/types';
import { EDAAD_CONFIG } from '../../edaadConfig';

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || 'https://mock.supabase.co',
  import.meta.env.VITE_SUPABASE_ANON_KEY || 'mock-key',
);

const EDAAD_CHATS_STORAGE_KEY = 'edaad_chat_ids_cache';

let cachedEdaadChatIds: Set<string> | undefined = undefined;

export function loadEdaadChatIds(): Set<string> {
  if (cachedEdaadChatIds) return cachedEdaadChatIds;

  try {
    const stored = localStorage.getItem(EDAAD_CHATS_STORAGE_KEY);
    if (stored) {
      const idsArray = JSON.parse(stored) as string[];
      cachedEdaadChatIds = new Set(idsArray);
      return cachedEdaadChatIds;
    }
  } catch (err) {
    // Ignore error
  }

  // Fallback / Initial State: We can mock some IDs here or leave empty.
  cachedEdaadChatIds = new Set<string>();
  return cachedEdaadChatIds;
}

export function saveEdaadChatIds(ids: string[]) {
  try {
    localStorage.setItem(EDAAD_CHATS_STORAGE_KEY, JSON.stringify(ids));
    cachedEdaadChatIds = new Set(ids);
  } catch (err) {
    // Ignore error
  }
}

export async function syncEdaadChats() {
  try {
    // 1. Get current session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // 2. Fetch allowed chat IDs from student_enrollments
    const { data: enrollments, error } = await supabase
      .from('student_enrollments')
      .select('telegram_chat_id')
      .eq('student_id', session.user.id);

    if (error) throw error;

    if (enrollments) {
      const allowedChatIds = enrollments.map((e) => e.telegram_chat_id);
      saveEdaadChatIds(allowedChatIds);
    }
  } catch {
    // Sync failed silently
  }
}

export function isEdaadChat(chat: ApiChat): boolean {
  // Check against statically configured allowed IDs
  if (EDAAD_CONFIG.ALLOWED_CHAT_IDS.includes(chat.id) || 
      (chat.title && EDAAD_CONFIG.ALLOWED_CHAT_IDS.includes(chat.title))) {
    return true;
  }

  // Check against dynamically loaded IDs from Supabase
  const edaadIds = loadEdaadChatIds();
  if (edaadIds.size > 0 && edaadIds.has(chat.id)) {
    return true;
  }

  return false;
}
